import DateFnsUtils from '@date-io/date-fns';
import { Box, Button, Grid, LinearProgress, Tooltip, Zoom } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import 'date-fns';
import { Field, Form, Formik, FormikValues } from 'formik';
import { TextField } from 'formik-material-ui';
import { KeyboardDateTimePicker } from 'formik-material-ui-pickers';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getGlobal } from 'reactn';
import { RetailerOptConfigStatusEnum, ConversionType } from '../../gen/api/api';
import { CAN_EDIT } from '../../shared/constants/AuthConstants';
import {
    ALLOWED_STATUS_TRANSITIONS,
    TOOLTIPS,
    STATUS_LABEL,
    TYPE_LABEL,
} from '../../shared/constants/GeneralDataFormConstants';
import { ENDDATETIME_FIELD_AFTER_STARTDATE_MESSAGE } from '../../shared/constants/ValidationMessagesConstants';
import { DatePickerFieldEditor } from '../field-editors/DatePickerFieldEditor';
import OptionFieldEditor from '../field-editors/OptionFieldEditor';
import RulesComponent from '../rule-components/RulesComponent';
import styles from './ConfigurationForm.module.css';
import useConfigurationForm from './ConfigurationFormHooks';
import {
    configurationFormInitialValues,
    ConfigurationFormProps,
    RetailerOptConfigRich,
} from './ConfigurationFormSchema';
import ApiAutocomplete from './ApiAutocomplete';
import useFeatures from '../../helpers/features';
import ApiService from '../../services/ApiService';

const { featureEnabledGrocerPointsConversion } = useFeatures();

const ConfigurationForm: FunctionComponent<ConfigurationFormProps> = ({
    ruleDefinitions,
    rules = [],
    onRulesInitialized,
    setDirty,
}: ConfigurationFormProps) => {
    const [initialFactValues, setInitialFactValues] = useState({});
    const [rulesInitialized, setRulesInitialized] = useState(false);

    const { configurationId } = useParams();

    const {
        fetchConfiguration,
        configurationFormValidate,
        configurationFormSubmit,
        getInitialFactValues,
        formDisabled,
    } = useConfigurationForm();

    const [selectedConfiguration, setSelectedConfiguration] = useState<RetailerOptConfigRich>(
        configurationFormInitialValues,
    );

    useEffect(() => {
        setInitialFactValues(getInitialFactValues(ruleDefinitions || [], rules));
        const fetchData = async () => {
            if (configurationId) {
                await fetchConfiguration(configurationId, ruleDefinitions || [], setSelectedConfiguration);
            }
            setRulesInitialized(true);
            onRulesInitialized && onRulesInitialized();
        };
        fetchData();
    }, []);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={{ ...selectedConfiguration, ...initialFactValues }}
            onSubmit={(values: RetailerOptConfigRich, _actions: any) => {
                return configurationFormSubmit(values, ruleDefinitions, rules);
            }}
            validate={configurationFormValidate}
            validateOnBlur={true}
        >
            {({ isSubmitting, isValid, dirty }) => (
                <div className={styles['general-data-form']}>
                    {setDirty && setDirty(dirty)}
                    <Form>
                        <Grid container spacing={0}>
                            <Box width={1} p={1} m={1} boxShadow={3}>
                                <Grid container spacing={1}>
                                    <Grid item xs={6}>
                                        { featureEnabledGrocerPointsConversion() &&
                                            <div>
                                                <Tooltip
                                                    title={TOOLTIPS['type']}
                                                    id={'tooltip-type'}
                                                    key={'tooltip-type'}
                                                    arrow
                                                    placement="top"
                                                    TransitionComponent={Zoom}
                                                    disableHoverListener
                                                >
                                                    <div>
                                                        <Field
                                                            component={OptionFieldEditor}
                                                            required
                                                            id="ConversionType"
                                                            fieldName="type"
                                                            fieldLabel="Conversion Type"
                                                            fieldOptions={() => [
                                                                {
                                                                    value: ConversionType.CURRENCYCONVERSION,
                                                                    label: TYPE_LABEL.get(ConversionType.CURRENCYCONVERSION),
                                                                },
                                                                {
                                                                    value: ConversionType.GROCERPOINTSCONVERSION,
                                                                    label: TYPE_LABEL.get(
                                                                        ConversionType.GROCERPOINTSCONVERSION,
                                                                    ),
                                                                },
                                                                {
                                                                    value: ConversionType.FISCONVERSION,
                                                                    label: TYPE_LABEL.get(
                                                                        ConversionType.FISCONVERSION
                                                                    )
                                                                }
                                                            ]}
                                                            variant="filled"
                                                            disabled={formDisabled(selectedConfiguration.status)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        }
                                    </Grid>

                                    <Grid item xs={6}></Grid>

                                    <Grid item xs={6}>
                                        <Field
                                            component={TextField}
                                            name="id"
                                            id="id"
                                            label="ID"
                                            variant="filled"
                                            type="text"
                                            InputProps={{
                                                readOnly: true,
                                            }}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                            disabled={formDisabled(selectedConfiguration.status)}
                                            fullWidth
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Tooltip
                                            title={TOOLTIPS['name']}
                                            id={'tooltip-name'}
                                            key={'tooltip-name'}
                                            arrow
                                            placement="top"
                                            TransitionComponent={Zoom}
                                            disableHoverListener
                                        >
                                            <div>
                                                <Field
                                                    component={TextField}
                                                    id="name"
                                                    name="name"
                                                    label="Name"
                                                    title=""
                                                    variant="filled"
                                                    type="text"
                                                    fullWidth
                                                    required
                                                    disabled={formDisabled(selectedConfiguration.status)}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Tooltip
                                            title={TOOLTIPS['program']}
                                            id={'tooltip-program'}
                                            key={'tooltip-program'}
                                            arrow
                                            placement="top"
                                            TransitionComponent={Zoom}
                                            disableHoverListener
                                        >
                                            <div>
                                                <ApiAutocomplete
                                                    fieldName="program"
                                                    fieldLabel="Program"
                                                    optionsMethod={(searchTerm?: string, values?: FormikValues) => {return ApiService().fetchPrograms(searchTerm)}}
                                                    required={true}
                                                    disabled={formDisabled(selectedConfiguration.status)}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Tooltip
                                            title={TOOLTIPS['participant']}
                                            id={'tooltip-participant'}
                                            key={'tooltip-participant'}
                                            arrow
                                            placement="top"
                                            TransitionComponent={Zoom}
                                            disableHoverListener
                                        >
                                            <div>
                                                <ApiAutocomplete
                                                    fieldName="retailer"
                                                    fieldLabel="Participant"
                                                    optionsMethod={(searchTerm?: string, values?: FormikValues) => {return ApiService().fetchRetailers(searchTerm)}}
                                                    required={true}
                                                    disabled={formDisabled(selectedConfiguration.status)}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <Grid container justify="space-around">
                                                <Tooltip
                                                    title={TOOLTIPS['start']}
                                                    id={'tooltip-start'}
                                                    key={'tooltip-start'}
                                                    arrow
                                                    placement="top"
                                                    TransitionComponent={Zoom}
                                                    disableHoverListener
                                                >
                                                    <div>
                                                        <Field
                                                            component={KeyboardDateTimePicker}
                                                            format="MM/dd/yyyy hh:mm a"
                                                            margin="dense"
                                                            id="startDateTime"
                                                            label="Start"
                                                            KeyboardButtonProps={{
                                                                'aria-label': 'change date',
                                                            }}
                                                            inputVariant="filled"
                                                            required
                                                            name="startDateTime"
                                                            disabled={formDisabled(selectedConfiguration.status)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </Grid>
                                        </MuiPickersUtilsProvider>
                                    </Grid>

                                    <Grid item xs={3}>
                                        <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                            <Grid container justify="space-around">
                                                <Tooltip
                                                    title={TOOLTIPS['end']}
                                                    id={'tooltip-end'}
                                                    key={'tooltip-end'}
                                                    arrow
                                                    placement="top"
                                                    TransitionComponent={Zoom}
                                                    disableHoverListener
                                                >
                                                    <div>
                                                        <Field
                                                            component={DatePickerFieldEditor}
                                                            margin="dense"
                                                            id="endDateTime"
                                                            label="End"
                                                            minDateMessage={ENDDATETIME_FIELD_AFTER_STARTDATE_MESSAGE}
                                                            KeyboardButtonProps={{
                                                                'aria-label': 'change date',
                                                            }}
                                                            inputVariant="filled"
                                                            required
                                                            strictCompareDates
                                                            name="endDateTime"
                                                            disabled={formDisabled(selectedConfiguration.status)}
                                                        />
                                                    </div>
                                                </Tooltip>
                                            </Grid>
                                        </MuiPickersUtilsProvider>
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Tooltip
                                            title={TOOLTIPS['description']}
                                            id={'tooltip-description'}
                                            key={'tooltip-description'}
                                            arrow
                                            placement="top"
                                            TransitionComponent={Zoom}
                                            disableHoverListener
                                        >
                                            <div>
                                                <Field
                                                    component={TextField}
                                                    name="description"
                                                    id="description"
                                                    label="Description"
                                                    variant="filled"
                                                    type="text"
                                                    rows="4"
                                                    fullWidth
                                                    multiline
                                                    disabled={formDisabled(selectedConfiguration.status)}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>

                                    <Grid item xs={6}></Grid>

                                    <Grid item xs={6}>
                                        <Tooltip
                                            title={TOOLTIPS['status']}
                                            id={'tooltip-status'}
                                            key={'tooltip-status'}
                                            arrow
                                            placement="top"
                                            TransitionComponent={Zoom}
                                            disableHoverListener
                                        >
                                            <div>
                                                <Field
                                                    component={OptionFieldEditor}
                                                    required
                                                    id="Status"
                                                    fieldName="status"
                                                    fieldLabel="Status"
                                                    fieldOptions={() => [
                                                        {
                                                            value: RetailerOptConfigStatusEnum.DRAFT,
                                                            label: STATUS_LABEL.get(RetailerOptConfigStatusEnum.DRAFT),
                                                        },
                                                        {
                                                            value: RetailerOptConfigStatusEnum.ENABLED,
                                                            label: STATUS_LABEL.get(
                                                                RetailerOptConfigStatusEnum.ENABLED,
                                                            ),
                                                        },
                                                        {
                                                            value: RetailerOptConfigStatusEnum.DISABLED,
                                                            label: STATUS_LABEL.get(
                                                                RetailerOptConfigStatusEnum.DISABLED,
                                                            ),
                                                        },
                                                    ]}
                                                    getOptionDisabled={(option: {
                                                        value: RetailerOptConfigStatusEnum;
                                                        label: string;
                                                    }) => {
                                                        return !ALLOWED_STATUS_TRANSITIONS.get(
                                                            selectedConfiguration.status,
                                                        )?.includes(option.value);
                                                    }}
                                                    variant="filled"
                                                    disabled={!getGlobal().keycloakState.isAuthorized(CAN_EDIT)}
                                                />
                                            </div>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Grid>

                        {rulesInitialized && (
                            <RulesComponent
                                disabled={formDisabled(selectedConfiguration.status)}
                                ruleDefinitions={ruleDefinitions || []}
                            />
                        )}

                        <Grid container item xs={12}>
                            {isSubmitting && <LinearProgress />}
                        </Grid>

                        <Grid container item xs={12} justify="flex-end">
                            <Button
                                size="large"
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={!isValid || isSubmitting || !getGlobal().keycloakState.isAuthorized(CAN_EDIT)}
                            >
                                Submit
                            </Button>
                        </Grid>
                    </Form>
                </div>
            )}
        </Formik>
    );
};

export default ConfigurationForm;
